import {
  CalendarOutlineIcon,
  UnstyledInput,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import type {
  ChangeEventHandler,
  MouseEventHandler,
} from "react"
import {
  useCallback,
  useMemo,
  useRef,
} from "react"
import {
  useGraphContext,
  graphEndPadding,
} from "../GraphContext"
import styles from "./GraphHeaderButton.module.scss"
import { useReadableDate } from "src/hooks"

const id = "graph-date-input"
const dateInputFormat = "YYYY-MM-DD"

export function GraphDateButton() {
  const {
    graphStart,
    graphEnd,
    listRef,
    graphScrollDate,
    zoom,
    pointsPerHour,
  } = useGraphContext()

  const dateInputRef = useRef<HTMLInputElement>(null)

  const graphDate: string = useMemo(
    () => {
      const diffInPixels = dayjs(graphEnd)
        .diff(
          graphScrollDate,
          "hours",
          true,
        ) * pointsPerHour

      const isNowVisible = diffInPixels < (zoom * pointsPerHour / 2) + graphEndPadding

      if (isNowVisible) {
        return dayjs().format(dateInputFormat)
      }

      return dayjs(graphScrollDate).format(dateInputFormat)
    },
    [
      graphScrollDate,
      zoom,
      pointsPerHour,
      graphEnd,
    ],
  )

  const readableDateString = useReadableDate(graphScrollDate.toISOString())

  const openDatePicker: MouseEventHandler<HTMLButtonElement> = useCallback(
    () => {
      dateInputRef.current?.focus()
      dateInputRef.current?.showPicker()
    },
    [dateInputRef],
  )

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (ev) => {
      const now = dayjs()
      const day = dayjs(ev.target.value)
        .startOf("day")
        .hour(12)

      const isToday = day.isSame(
        now,
        "day",
      )

      if (isToday) {
        listRef.current?.scrollTo(0)
      } else {
        const diffInHours = dayjs(graphEnd)
          .diff(
            day,
            "hours",
            true,
          ) - (zoom / 2) // this centers the desired date

        listRef.current?.scrollTo(diffInHours * pointsPerHour)
      }
    },
    [
      listRef,
      zoom,
      pointsPerHour,
      graphEnd,
    ],
  )

  return (
    <div>
      <UnstyledInput
        ref={dateInputRef}
        className={styles.input}
        onChange={onChange}
        value={graphDate}
        id={id}
        type="date"
        min={dayjs(graphStart).format(dateInputFormat)}
        max={dayjs(graphEnd).format(dateInputFormat)}
        tabIndex={-1}
      />
      <button
        className={styles.button}
        onClick={openDatePicker}
        id="graph-date-button"
      >
        {readableDateString}
        <CalendarOutlineIcon />
      </button>
    </div>
  )
}
